import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../i18n";
import { Languages } from "lucide-react";

export default function Register() {
    const { register } = useAuth();
    const { t, lang, toggleLang } = useI18n();
    const navigate = useNavigate();
    const [full_name, setFullName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            await register(email, password, full_name);
            navigate("/chat");
        } catch (e) {
            setErr(e?.response?.data?.detail || t.auth.registerError);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-background">
            <div className="w-full max-w-md">
                <div className="flex items-center justify-between mb-10">
                    <Link to="/login" className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-display font-bold">F</div>
                        <span className="font-display text-lg font-semibold">FiscoAI</span>
                    </Link>
                    <button
                        data-testid="auth-language-switcher"
                        onClick={toggleLang}
                        className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-muted-foreground hover:text-foreground"
                    >
                        <Languages size={14} /> {lang === "it" ? "IT → AR" : "AR → IT"}
                    </button>
                </div>
                <h2 className="font-display text-3xl font-medium tracking-tight mb-2">{t.auth.registerTitle}</h2>
                <p className="text-sm text-muted-foreground mb-8">{t.auth.registerSubtitle}</p>

                <form onSubmit={submit} className="space-y-4" data-testid="register-form">
                    <Field label={t.auth.fullName}>
                        <input
                            data-testid="register-fullname"
                            required
                            value={full_name}
                            onChange={(e) => setFullName(e.target.value)}
                            className="w-full px-4 py-3 rounded-md border border-border bg-card focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-sm"
                            placeholder="Mario Rossi"
                        />
                    </Field>
                    <Field label={t.auth.email}>
                        <input
                            data-testid="register-email"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full px-4 py-3 rounded-md border border-border bg-card focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-sm"
                        />
                    </Field>
                    <Field label={t.auth.password}>
                        <input
                            data-testid="register-password"
                            type="password"
                            required
                            minLength={6}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full px-4 py-3 rounded-md border border-border bg-card focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-sm"
                            placeholder="Min. 6 caratteri"
                        />
                    </Field>
                    {err && <div data-testid="register-error" className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2">{err}</div>}
                    <button
                        type="submit"
                        data-testid="register-submit"
                        disabled={loading}
                        className="w-full px-6 py-3 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-medium text-sm transition-colors disabled:opacity-50"
                    >
                        {loading ? "..." : t.auth.registerBtn}
                    </button>
                </form>
                <div className="mt-6 text-sm text-muted-foreground text-center">
                    {t.auth.hasAccount}{" "}
                    <Link data-testid="register-to-login" to="/login" className="text-primary font-medium hover:underline">{t.auth.loginLink}</Link>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground mb-1.5 block">{label}</span>
            {children}
        </label>
    );
}
